import { ProductCardData } from '@/types/Product';
import { CACHE_TIME } from '@/const/cache';
import { Cart } from '@/types/Cart';

export function applyProductWishlistFlag(
  data: ProductCardData
): ProductCardData {
  return { ...data, showWishlist: data.tier !== 'Privee' };
}

const EMPTY_CART: Cart = {
  id: null,
  name: null,
  description: null,
  customerId: null,
  items: []
};

/**
 * Flag every cart item for the wishlist affordance.
 *
 * The argument is not guaranteed to be a well-formed Cart, so it is typed for
 * what actually arrives. `getCartByUserId` is typed `Cart | null` and hands
 * back the closet response as-is when it carries no `items` (it guards with
 * `cart?.items` before enriching), and the CART_REFRESH dispatch in
 * refreshUserDetails forwards whatever `/api/cart` returned after checking
 * only the HTTP status — that route answers 200 with `data: null` for a member
 * who has no cart record.
 *
 * Dereferencing `items` here therefore threw inside the ContextBroker reducer,
 * which takes down the whole tree via the app error boundary: a member with no
 * cart saw "Something went wrong" on an otherwise healthy page. Fall back to an
 * empty cart instead.
 */
export function applyCartWishlistFlags(cart?: Cart | null): Cart {
  return {
    ...EMPTY_CART,
    ...cart,
    items: (cart?.items ?? []).map((item) => ({
      ...item,
      productCardData: applyProductWishlistFlag(item.productCardData)
    }))
  };
}
import {
  EPCCOrderItem,
  EPCCOrderResponse,
  ORDER_ITEM_STATUS,
  ORDER_ITEM_STATUS_INTERNAL,
  RevolveOrderItem
} from '@/types/Order';
import { AddressData } from '@/types/Cart';
import type { RevolveOrderResponse } from '@/types/Revolve';
import { ELLA_RETAILER } from '@/components/common/EllaCartProduct/EllaCartProduct';

/**
 * Fetches the current Vivrelle cart data from the API
 * @returns Promise with the cart data
 */
export const fetchVivrelleCart = async (): Promise<Cart> => {
  try {
    const response = await fetch('/api/cart', {
      method: 'GET',
      headers: {
        'content-type': 'application/json'
      },
      next: { revalidate: CACHE_TIME.NO_CACHE }
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch cart: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();

    return data.data;
  } catch (error) {
    console.error('Error in fetchVivrelleCart:', error);
    throw new Error(
      error instanceof Error
        ? error.message
        : 'An unknown error occurred while fetching cart'
    );
  }
};

export const addItemToCart = async (
  revolveCartId: string | undefined,
  cartItem: ProductCardData,
  taxRate?: number
): Promise<
  | { revolveCartId?: string; cartItemId?: string; taxItemId?: string }
  | undefined
  | { error: string }
> => {
  try {
    if (cartItem.store?.match(/vivrelle/i)) {
      // Vivrelle items don't need a cart
      const response = await fetch('/api/cart/items', {
        method: 'POST',
        body: JSON.stringify({
          productId: cartItem.id
        }),
        headers: {
          'content-type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.body?.match(/error/i)) {
        return { error: data.body };
      }
      return { cartItemId: data.body[0].id };
    } else {
      // For non-Vivrelle items, we need a cart
      let newCartId = revolveCartId;
      if (!revolveCartId) {
        newCartId = await createCart();
        if (!newCartId) {
          throw new Error('Failed to create cart');
        }
      }

      const response = await fetch('/api/cart/add-item', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cartId: newCartId,
          sku: cartItem.sku || '',
          size: cartItem.size || '',
          name: cartItem.name || '',
          price: Number(cartItem.price || 0),
          product: cartItem
        })
      });
      const cartResponse = await response.json();

      // Find the newly added item by matching its properties
      const newlyAddedItem = cartResponse.data?.find(
        (item: any) =>
          item.sku === cartItem.sku &&
          item.name === cartItem.name &&
          item.custom_inputs?.size === cartItem.size
      );

      let taxItemId: string | undefined;
      if (taxRate && newCartId && newlyAddedItem?.id) {
        const taxItemResponse = await addTaxToCartItem(
          newCartId,
          taxRate,
          newlyAddedItem.id
        );
        taxItemId = taxItemResponse?.data?.id;
      }
      return {
        revolveCartId: newCartId,
        cartItemId: newlyAddedItem?.id,
        taxItemId: taxItemId
      };
    }
  } catch (error) {
    console.error('Error adding item to cart:', error);
    return undefined;
  }
};

export const updateCartItem = async (
  cartId: string | undefined,
  cartItem: {
    id: string;
    size: string;
    product: any;
    [key: string]: any;
  }
): Promise<void> => {
  if (!cartId) {
    throw new Error('Cart ID is required to update an item');
  }

  try {
    const response = await fetch('/api/cart/update-item', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        cartId,
        itemId: cartItem.id,
        size: cartItem.size,
        product: cartItem.product
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        typeof errorData === 'object' &&
        errorData !== null &&
        'message' in errorData
          ? String(errorData.message)
          : 'Failed to update cart item'
      );
    }
  } catch (error) {
    console.error('Error updating cart item:', error);
    throw error;
  }
};

export const removeItemFromCart = async (
  cartId: string | undefined,
  cartItemId: string,
  cartItemStore?: string
): Promise<void> => {
  try {
    if (cartItemStore?.match(/vivrelle/i)) {
      await fetch('/api/cart/items', {
        method: 'DELETE',
        body: JSON.stringify({
          cartItemId
        }),
        headers: {
          'content-type': 'application/json'
        }
      });
    } else if (cartId && cartItemId) {
      await fetch(`/api/cart/${cartId}/items?id=${cartItemId}`, {
        method: 'DELETE',
        headers: {}
      });
    }
  } catch (error) {
    console.error('Error removing item from cart:', error);
  }
};

export const createCart = async () => {
  try {
    const response = await fetch('/api/cart/create', {
      method: 'POST'
    });
    const data = await response.json();
    return data.data?.id;
  } catch (error) {
    console.error('Error creating cart:', error);
    return null;
  }
};

/**
 * Get items from a generic cart
 * @param cartId The ID of the cart to get items from
 * @returns The cart data including items
 */
export const getGenericCartItems = async (cartId: string) => {
  try {
    const url = `/api/cart/${cartId}/items`;

    const response = await fetch(url);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error response from server:', {
        status: response.status,
        statusText: response.statusText,
        url,
        error: errorText
      });
      throw new Error(
        `Failed to fetch cart items: ${response.status} ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error('Error in getGenericCartItems:', error);
    throw error;
  }
};

/**
 * Get generic cart
 * @param cartId The ID of the cart to get items from
 * @returns The cart data tax items
 */
export const getGenericCart = async (cartId: string) => {
  try {
    const response = await fetch(`/api/cart/${cartId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch cart');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching cart items:', error);
    throw error;
  }
};

/**
 * Get order
 * @param orderId The ID of the order to get items from
 * @returns The order data tax items
 */
export const getOrder = async (
  orderId: string,
  withProducts: boolean = false
) => {
  try {
    const response = await fetch(`/api/order/${orderId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch order');
    }

    const order: EPCCOrderResponse = await response.json();
    let products;
    if (withProducts) {
      products = await Promise.all(
        order.included?.items?.map(async (item: EPCCOrderItem) => {
          const response = await fetch('/api/product?id=' + item?.product_id, {
            method: 'GET',
            headers: {
              'content-type': 'application/json'
            },
            next: { revalidate: CACHE_TIME.NO_CACHE }
          });
          const { data } = await response.json();

          return {
            product: {
              id: data.id,
              borrowed: true,
              brand: data.brand,
              name: data.productName,
              slug: data.slug,
              sku: data.sku,
              tier: data.productTier,
              mainImage: data.images[0],
              item_group_id: data.sku,
              price: 0,
              link: data.link || `/products/${data.slug}`,
              color: data.color[0],
              category: 'handbag',
              store: ELLA_RETAILER.REVOLVE
            },
            internalStatus: ORDER_ITEM_STATUS_INTERNAL.PROCESSING,
            status: ORDER_ITEM_STATUS.PROCESSING
          };
        }) || []
      );
    }

    products =
      products && products.filter((product: any) => product !== undefined);

    return {
      id: order.data.id,
      shortId: order.data.short_id,
      orderDate: new Date(order.data['order-created-date']),
      shippingAddress: order.data.shipping_address,
      orderItems: products || [],
      shippingMethod: order.data['shipping-methods'],
      status: order.data['internal-order-status']
    };
  } catch (error) {
    console.error('Error fetching order items:', error);
    throw error;
  }
};

/**
 * Get generic order
 * @param orderId The ID of the order to get items from
 * @returns The order data tax items
 */
export const getGenericOrder = async (orderId: string) => {
  try {
    const response = await fetch(`/api/order/${orderId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch order');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching order items:', error);
    throw error;
  }
};

/**
 * Checkout a generic cart
 * @param cartId The ID of the cart to checkout
 * @param shippingAddress Optional shipping address data
 * @param billingAddress Optional billing address data
 * @returns The checkout result
 */
export const checkoutGenericCart = async (
  cartId: string,
  shippingAddress?: AddressData,
  billingAddress?: AddressData
) => {
  try {
    // Convert any legacy address format to the new format
    const formattedShippingAddress = shippingAddress
      ? formatAddress(shippingAddress)
      : undefined;
    const formattedBillingAddress = billingAddress
      ? formatAddress(billingAddress)
      : undefined;

    const checkoutResponse = await fetch('/api/cart/generic/checkout', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        cartId,
        shippingAddress: formattedShippingAddress ?? undefined,
        billingAddress: formattedBillingAddress ?? undefined
      })
    });

    if (!checkoutResponse.ok) {
      const errorData = await checkoutResponse.json();
      console.error('Checkout error:', errorData);
      throw new Error(`Failed to checkout cart: ${checkoutResponse.status}`);
    }

    return await checkoutResponse.json();
  } catch (error) {
    console.error('Error checking out cart:', error);
    throw error;
  }
};

/**
 * Format address to match the API's expected format
 */
const formatAddress = (address: any): AddressData => {
  // Handle both legacy and new format
  return {
    first_name: address.first_name ?? address.firstName ?? undefined,
    last_name: address.last_name ?? address.lastName ?? undefined,
    phone: address.phone ?? address.phoneNumber ?? undefined,
    line_1: address.line_1 ?? address.addressLine1 ?? undefined,
    line_2: address.line_2 ?? address.addressLine2 ?? undefined,
    city: address.city ?? undefined,
    region: address.region ?? address.state ?? undefined,
    postcode: address.postcode ?? address.zipCode ?? undefined,
    country: address.country ?? undefined
  };
};

/**
 * Updates the tax for a specific item in the cart
 * @param cartId - The ID of the cart
 * @param itemId - The ID of the item to update tax for
 * @param taxRate - The new tax rate to apply (as a decimal, e.g., 0.08 for 8%)
 * @param taxItemId - The ID of the tax item to update
 * @returns Promise with the updated cart
 */
export const updateCartItemTax = async (
  cartId: string,
  itemId: string,
  taxRate: number,
  taxItemId: string
) => {
  try {
    // Call our API endpoint to handle the tax update
    const response = await fetch('/api/cart/update-tax', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        cartId,
        itemId,
        taxRate,
        taxItemId
      })
    });

    if (!response.ok) {
      const error = await response.text();
      console.error('Failed to update cart item tax:', error);
      throw new Error('Failed to update cart item tax');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating cart item tax:', error);
    throw error;
  }
};

export const getTaxRateFromStripe = async (address: AddressData) => {
  try {
    const taxResponse = await fetch('/api/cart/tax', {
      method: 'POST',
      next: { revalidate: CACHE_TIME.NO_CACHE },
      body: JSON.stringify({
        // Based on shipping address
        // https://stripe.com/resources/more/should-you-charge-sales-tax-based-on-the-billing-or-shipping-address#using-shipping-vs-billing-address-when-selling-tangible-goods
        street1: address.line_1,
        street2: address.line_2,
        city: address.city,
        state: address.region,
        country: 'us',
        zip: address.postcode
      })
    });

    const json = (await taxResponse.json()) as {
      success: boolean;
      data: number;
    };

    return json.data;
  } catch (error) {
    console.error(error);
    return;
  }
};

/**
 * Add tax to a cart item
 * @param cartId The ID of the cart
 * @param rate The tax rate to apply (as a decimal, e.g., 0.08 for 8%)
 * @param cartItemId The ID of the cart item to add tax to
 * @returns The response from the API
 */

/** Email and addresses are derived server-side from the session. */
export const submitRevolveOrder = async (
  items: RevolveOrderItem[]
): Promise<RevolveOrderResponse> => {
  try {
    const response = await fetch('/api/revolve/order-submit', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ items })
    });

    const data = await response.json();

    // if (!response.ok || !data.success) {
    //   throw new Error(
    //     data.payment?.message || 'Failed to submit order to Revolve'
    //   );
    // }

    return data;
  } catch (error) {
    console.error('Error in submitRevolveOrder:', error);
    // throw new Error(
    //   error instanceof Error
    //     ? error.message
    //     : 'An unknown error occurred while submitting the order'
    // );
  }
};

export const addTaxToCartItem = async (
  cartId: string,
  rate: number,
  cartItemId: string
) => {
  try {
    const response = await fetch('/api/cart/add-tax-item', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        cartId,
        cartItemId,
        rate
      })
    });

    if (!response.ok) {
      const error = await response.text();
      console.error('Failed to add tax to cart item:', error);
      throw new Error('Failed to add tax to cart item');
    }

    return await response.json();
  } catch (error) {
    console.error('Error adding tax to cart item:', error);
    throw error;
  }
};

/**
 * Process revolve order response and update cart items
 * @param response The response from submitRevolveOrder
 * @param cartId The ID of the cart
 * @param cartItems Current items in the cart
 * @returns Promise that resolves when all operations are complete
 */
export const processRevolveOrderResponse = async (
  response: RevolveOrderResponse,
  cartId: string,
  cartItems: Array<{
    id: string;
    product: { sku: string; size: string; store?: string };
  }>
): Promise<void> => {
  // Get all successfully processed items from all stores
  const instockItems = [
    ...(response?.results?.revolve?.success
      ? response.results.revolve.data?.orders?.instock?.items || []
      : []),
    ...(response?.results?.fwrd?.success
      ? response.results.fwrd.data?.orders?.instock?.items || []
      : [])
  ];

  // Get store status to identify failed stores
  const storeStatus = {
    revolve: response?.results?.revolve?.success,
    fwrd: response?.results?.fwrd?.success
  };

  try {
    // Create a Set of SKU+size combinations that were successfully processed
    const processedItems = new Set(
      instockItems
        .filter(
          (
            item
          ): item is {
            style: string;
            size: string;
            id: string;
            status: string;
          } => Boolean(item?.style && item?.size)
        )
        .map((item) => `${item.style}:${item.size}`)
    );

    // Remove items that:
    // 1. Weren't in the successful response, OR
    // 2. Belong to a store that had a failure
    const removalPromises = cartItems.map(async (item) => {
      const itemKey = `${item.product.sku}:${item.product.size}`;
      const itemStore = item.product.store?.toLowerCase();

      const shouldRemove =
        !processedItems.has(itemKey) || // Item wasn't in successful response
        (itemStore &&
          storeStatus[itemStore as keyof typeof storeStatus] === false); // Store had a failure

      if (shouldRemove) {
        await removeItemFromCart(cartId, item.id);
      }
    });

    await Promise.all(removalPromises);
  } catch (error) {
    console.error('Error processing revolve order response:', error);
    throw error;
  }
};

export const cancelRevolveOrders = async (
  invoices: { invoice: string; store: string }[]
) => {
  if (invoices.length > 0) {
    try {
      await Promise.all(
        invoices.map((invoice) =>
          fetch('/api/revolve/order-cancel', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              invoice: invoice.invoice,
              store: invoice.store
            })
          }).catch((error: Error) => {
            console.error(`Failed to cancel invoice ${invoice}:`, error);
            return { ok: false, error };
          })
        )
      );
    } catch (error) {
      console.error('Error in cancelRevolveOrders:', error);
    }
  }
};
